import { Box, Button, List, ListItem, ListItemText, Paper, Typography, ListItemIcon, TextField, Accordion, AccordionSummary, AccordionDetails, Grid, Select, MenuItem, Chip, OutlinedInput, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { Link, useLoaderData, useParams } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect } from "react";
import ViewTableInBigQueryButton from 'components/bigquery/ViewTableInBigQueryButton';

import EmptyStatePlaceholder from "components/EmptyStatePlaceholder";
import PageHeader from "components/PageHeader";
import buildRoute from "components/buildRoute";
import Divider from "@mui/material/Divider";
import { getCsrfToken } from "utils/csrf";
import React from "react";

function ListCutterJobsIndexPage() {
  const { jobs: initialJobs } = useLoaderData();
  const [jobs, setJobs] = useState(initialJobs);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStates, setSelectedStates] = useState([]);
  const [advancedSearch, setAdvancedSearch] = useState({
    sourceDataset: '',
    sourceTable: '',
    targetDataset: '',
    targetTable: '',
    createdAfter: '',
    createdBefore: ''
  });
  
  const { projectId } = useParams();
  
  useEffect(() => {
    const updateJobStatuses = async () => {
      try {
        setIsLoading(true);
        const pathProjectId = window.location.pathname.split('/')[2];
        
        const csrfToken = getCsrfToken();
        
        const response = await fetch('/list-cutter/api/update-job-statuses/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          mode: 'same-origin',
          body: JSON.stringify({ project_id: pathProjectId }),
        });

        if (!response.ok) {
          throw new Error(`Failed to update job statuses: ${response.status} ${response.statusText}`);
        }

        const jobsResponse = await fetch('/list-cutter/api/jobs/');
        if (!jobsResponse.ok) {
          throw new Error('Failed to fetch jobs');
        }
        const updatedJobs = await jobsResponse.json();
        setJobs(updatedJobs);
      } catch (error) {
        console.error('Error updating job statuses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    updateJobStatuses();
  }, []);
  
  const JOB_STATES = [
    { value: 'DONE', label: 'SUCCESS' },
    { value: 'FAILED', label: 'FAILED' },
    { value: 'PENDING', label: 'IN PROGRESS' }
  ];

  const filteredJobs = jobs.filter(job => {
    // State filter
    const stateMatch = selectedStates.length === 0 || selectedStates.includes(job.state);
    
    // Name search
    const nameMatch = job.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Advanced search filters
    const sourceDatasetMatch = !advancedSearch.sourceDataset || 
      job.source_dataset.toLowerCase().includes(advancedSearch.sourceDataset.toLowerCase());
    const sourceTableMatch = !advancedSearch.sourceTable || 
      job.source_table.toLowerCase().includes(advancedSearch.sourceTable.toLowerCase());
    const targetDatasetMatch = !advancedSearch.targetDataset || 
      job.target_dataset.toLowerCase().includes(advancedSearch.targetDataset.toLowerCase());
    const targetTableMatch = !advancedSearch.targetTable || 
      job.target_table.toLowerCase().includes(advancedSearch.targetTable.toLowerCase());
    
    // Date range filtering
    const jobDate = new Date(job.created);
    const afterDate = advancedSearch.createdAfter ? new Date(advancedSearch.createdAfter) : null;
    const beforeDate = advancedSearch.createdBefore ? new Date(advancedSearch.createdBefore) : null;
    
    const dateMatch = (
      (!afterDate || jobDate >= afterDate) &&
      (!beforeDate || jobDate <= beforeDate)
    );

    return stateMatch && nameMatch && sourceDatasetMatch && sourceTableMatch && 
           targetDatasetMatch && targetTableMatch && dateMatch;
  });

  const handleAdvancedSearchChange = (field) => (event) => {
    setAdvancedSearch(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getStatusIcon = (state, errors) => {
    if (state === 'DONE' && !errors) {
      return <CheckCircleIcon sx={{ color: 'success.main' }} />;
    } else if (state === 'DONE' && errors) {
      // Job is done but has errors - show as failed
      return <CancelIcon sx={{ color: 'error.main' }} />;
    } else if (state === 'FAILED') {
      return <CancelIcon sx={{ color: 'error.main' }} />;
    } else {
      return <PendingIcon sx={{ color: 'info.main' }} />;
    }
  };

  const CreateJobButton = () => {
    const buttonLabel = "Create New Job";
    return (
      <Button
        component={Link}
        to="new"
        variant="contained"
        color="secondary"
        aria-label={buttonLabel}
      >
        {buttonLabel}
      </Button>
    );
  };

  return (
    <>
      <Box sx={{ pt: 4 }}>
        <PageHeader
          title="List Cutter"
          subtitle="The easier, cooler way to create lists."
          action={<CreateJobButton />}
        />
      </Box>
      <Box component="section">
        {jobs.length === 0 ? (
          <EmptyStatePlaceholder
            title={"No List Cutter jobs to show"}
            subtitle={"Once you create a List Cutter job, it will show up here."}
          />
        ) : isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 8,
            minHeight: '300px'
          }}>
            <CircularProgress size={40} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">
              Loading jobs...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Retrieving the latest status for your List Cutter jobs
            </Typography>
          </Box>
        ) : (
          <>
            <Divider 
              sx={{ 
                mt: 4, 
                mb: 4,
                borderBottomWidth: 2,
                borderColor: 'grey.400'
              }} 
            />

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: '#f8f9fa',
                  '&:hover': { backgroundColor: '#f0f1f2' }
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Search Jobs (click to expand)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  mb: 3,
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start'
                }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="state-filter-label">Filter by Status</InputLabel>
                    <Select
                      labelId="state-filter-label"
                      multiple
                      value={selectedStates}
                      onChange={(e) => setSelectedStates(e.target.value)}
                      input={<OutlinedInput label="Filter by Status" />}
                      size="small"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={JOB_STATES.find(state => state.value === value)?.label || value}
                              size="small"
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {JOB_STATES.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    placeholder="Search jobs by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'grey.300' },
                        '&:hover fieldset': { borderColor: 'grey.400' },
                      },
                    }}
                  />
                </Box>

                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#f8f9fa',
                      '&:hover': { backgroundColor: '#f0f1f2' }
                    }}
                  >
                    <Typography>Advanced Search</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Source Dataset"
                          value={advancedSearch.sourceDataset}
                          onChange={handleAdvancedSearchChange('sourceDataset')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Source Table"
                          value={advancedSearch.sourceTable}
                          onChange={handleAdvancedSearchChange('sourceTable')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Target Dataset"
                          value={advancedSearch.targetDataset}
                          onChange={handleAdvancedSearchChange('targetDataset')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Target Table"
                          value={advancedSearch.targetTable}
                          onChange={handleAdvancedSearchChange('targetTable')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Created After"
                          type="date"
                          value={advancedSearch.createdAfter}
                          onChange={handleAdvancedSearchChange('createdAfter')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Created Before"
                          type="date"
                          value={advancedSearch.createdBefore}
                          onChange={handleAdvancedSearchChange('createdBefore')}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </AccordionDetails>
            </Accordion>

            <Typography 
              variant="body1" 
              color="text.primary"
              sx={{ 
                mt: 4,
                mb: 3 
              }}
            >
              Click on any job to view its details and start another job with the same settings.
            </Typography>
            <Paper sx={{ p: 0 }}>
              <List sx={{ 
                p: 2,
                bgcolor: 'background.default'
              }}>
                {filteredJobs.length === 0 ? (
                  <ListItem>
                    <ListItemText 
                      primary={
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          No jobs match your search
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  filteredJobs.map((job) => (
                    <ListItem 
                      key={job.id}
                      sx={{ 
                        mb: 2,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component={Link}
                        to={`${job.id}`}
                        sx={{
                          display: 'flex', 
                          alignItems: 'center',
                          flexGrow: 1,
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ pl: 1, pr: 2 }}>
                          {getStatusIcon(job.state, job.errors)}
                        </ListItemIcon>
                        
                        <ListItemText
                          sx={{ flexGrow: 1 }}
                          primary={
                            <Typography 
                              variant="body1"
                              sx={{
                                fontSize: '1rem',
                                wordBreak: 'break-word',
                                fontWeight: 500,
                                mb: 0.5,
                                color: 'text.primary',
                              }}
                            >
                              {job.name}
                            </Typography>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                Source: {job.source_dataset}.{job.source_table}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Results: {job.target_dataset}.{job.target_table}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Created: {new Date(job.created).toLocaleString()}
                              </Typography>
                              <Typography component="span" variant="body2" color="text.secondary" display="block">
                                Status: {
                                  job.state === 'DONE' && job.errors
                                    ? 'FAILED'
                                    : job.state === 'DONE'
                                      ? 'SUCCESS'
                                      : job.state || 'NOT STARTED'
                                }
                              </Typography>
                            </React.Fragment>
                          }
                        />
                      </Box>
                      
                      {job.state === 'DONE' && !job.errors && (
                        <Box 
                          sx={{ 
                            ml: 2,
                            pr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%'
                          }}
                        >
                          <ViewTableInBigQueryButton
                            projectId={job.project_id || window.location.pathname.split('/')[2]}
                            datasetId={job.target_dataset}
                            tableId={job.target_table}
                            buttonText="View in BigQuery"
                            variant="outlined"
                            size="small"
                            sx={{ 
                              py: 0.3, 
                              px: 1.5, 
                              fontSize: '0.75rem',
                              lineHeight: 1.2,
                              textTransform: 'none',
                              whiteSpace: 'nowrap'
                            }}
                          />
                        </Box>
                      )}
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>
          </>
        )}
      </Box>
    </>
  );
}

const routeArgs = {
  element: <ListCutterJobsIndexPage />,
  loader: async () => {
    const response = await fetch('/list-cutter/api/jobs/');
    if (!response.ok) {
      throw new Error('Failed to fetch jobs');
    }
    const jobs = await response.json();
    return { jobs };
  },
};

export default buildRoute(routeArgs);
